import { useCallback, useMemo, useState } from 'react';
import { IconButton, Paper, Stack, Typography } from '@mui/material';
import { Close, KeyboardArrowDown } from '@mui/icons-material';

import useGenerateCopy from 'hooks/mutations/useGenerateCopy';
import type { CuredApiError } from 'models/types';

import BrandNotRegisteredCTA from './BrandNotRegisteredCTA';
import ChatComposer from './ChatComposer';
import MessagesDisplay from './MessagesDisplay';
import SaraCircleMark from './SaraCircleMark';
import { ChatMessage, COPY_GENERATOR_ERROR_CODE_BRAND_NOT_REGISTERED, CopyGeneratorChatState } from './utils';

const CHAT_HEADER_BG = '#133035';

export type Props = {
    open: boolean;
    onClose: () => void;
};

/**
 * Shell for the Ask Sara copy-generator chat (email, SMS, and other content builders).
 * Call sites should gate rendering with LaunchDarkly `ai-brand-tools` (or equivalent).
 */
const CopyGeneratorChat = ({ open, onClose }: Props) => {
    const [composerText, setComposerText] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showBrandNotRegistered, setShowBrandNotRegistered] = useState(false);
    const chatState: CopyGeneratorChatState = useMemo(() => ({ channel: 'email', messages }), [messages]);

    const { mutate: generateCopy, isLoading } = useGenerateCopy({
        onSuccess: (response) => {
            setMessages((prev) => [...prev, { role: response.role, content: response.content }]);
            setErrorMessage(null);
            setShowBrandNotRegistered(false);
        },
        onError: (error) => {
            const code = (error as CuredApiError).errorCode;
            if (code === COPY_GENERATOR_ERROR_CODE_BRAND_NOT_REGISTERED) {
                setShowBrandNotRegistered(true);
                setErrorMessage(null);
                return;
            }
            setErrorMessage(error.message);
        },
    });

    const handleSend = useCallback(() => {
        const trimmed = composerText.trim();
        if (!trimmed) return;

        const userMessage: ChatMessage = { role: 'user', content: trimmed };
        const updatedMessages = [...messages, userMessage];

        setMessages(updatedMessages);
        setComposerText('');
        setErrorMessage(null);
        setShowBrandNotRegistered(false);
        generateCopy({ channel: 'email', messages: updatedMessages });
    }, [composerText, messages, generateCopy]);

    const handleRetry = useCallback(() => {
        if (isLoading || messages.length === 0) return;

        let lastAssistantIndex = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'assistant') {
                lastAssistantIndex = i;
                break;
            }
        }
        if (lastAssistantIndex === -1) return;

        const retryMessages = messages.slice(0, lastAssistantIndex);
        setMessages(retryMessages);
        setErrorMessage(null);
        setShowBrandNotRegistered(false);
        generateCopy({ channel: 'email', messages: retryMessages });
    }, [messages, isLoading, generateCopy]);

    const isSendDisabled = composerText.trim().length === 0 || isLoading;

    if (!open) {
        return null;
    }

    return (
        <Paper
            data-testid="copy-generator-chat-panel"
            elevation={8}
            role="dialog"
            aria-modal="false"
            aria-label="Ask Sara"
            sx={(theme) => ({
                position: 'fixed',
                left: theme.spacing(15),
                bottom: theme.spacing(11),
                width: 420,
                maxHeight: 'min(560px, calc(100vh - 120px))',
                zIndex: 1290,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
            })}
        >
            <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                sx={{
                    bgcolor: CHAT_HEADER_BG,
                    minHeight: 56,
                    flexShrink: 0,
                    px: 1,
                    pl: 2,
                }}
            >
                <Stack alignItems="center" direction="row" gap={1.5}>
                    <SaraCircleMark size={32} />
                    <Typography
                        component="h2"
                        sx={{ color: 'common.white', fontWeight: 500, fontSize: 16, lineHeight: 1.5 }}
                    >
                        Ask Sara
                    </Typography>
                </Stack>
                <Stack alignItems="center" direction="row">
                    <IconButton
                        aria-label="Minimize Ask Sara"
                        data-testid="copy-generator-chat-minimize"
                        size="small"
                        sx={{ color: 'common.white' }}
                        onClick={onClose}
                    >
                        <KeyboardArrowDown />
                    </IconButton>
                    <IconButton
                        aria-label="Close Ask Sara"
                        data-testid="copy-generator-chat-close"
                        size="small"
                        sx={{ color: 'common.white' }}
                        onClick={onClose}
                    >
                        <Close />
                    </IconButton>
                </Stack>
            </Stack>

            {showBrandNotRegistered ? (
                <BrandNotRegisteredCTA onDismiss={onClose} />
            ) : (
                <>
                    <MessagesDisplay
                        chatState={chatState}
                        errorMessage={errorMessage}
                        isLoading={isLoading}
                        onRetry={handleRetry}
                    />

                    <ChatComposer
                        value={composerText}
                        onChange={setComposerText}
                        onSend={handleSend}
                        disabled={isSendDisabled}
                    />
                </>
            )}
        </Paper>
    );
};

export default CopyGeneratorChat;
